import React from 'react';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookHeading from '../../../components/playbooks/PlaybookHeading';

const ManagerCommunityStage1 = () => {
  return (
    <div className="max-w-none text-gray-100">
      <PlaybookHeading
        title="Stage 1: Recognising the need"
        subtitle="Lay the groundwork for your community of practice by identifying key stakeholders and creating an initial plan."
      />
      
      <ExpandableCallout
        type="info"
        title="What do I need to DO in this stage?"
        defaultOpen={true}
      >
        <p className="text-base text-gray-300 leading-relaxed">
          You need to do 3 things:
        </p>
        <ol className="list-none space-y-2 text-gray-300">
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">1.</span>
            <span>Find the biggest pain for people managers that you can kill by creating a people manager community of practice.</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">2.</span>
            <span>Make that pain visible using data + story-telling.</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">3.</span>
            <span>Use the story to get both: executive sponsorship (if your org 'works' like that) AND people manager engagement and excitement to go for it.</span>
          </li>
        </ol>
        <p className="mt-4 text-base text-gray-300 leading-relaxed">Here's what we mean…</p>
      </ExpandableCallout>

      <div className="mt-8 space-y-6">
        <h2 className="text-xl font-semibold text-white mb-4">Triggers For Action</h2>
        <ExpandableCallout
          type="tip"
          title="Watch and Listen"
          defaultOpen={true}
        >
          <p className="text-base text-gray-300 leading-relaxed">
            These are things to watch and listen for. If you see and hear some of these, chances are you've got the perfect opportunity to initiate a people manager community of practice. Conversely, if you aren't seeing this, worth checking yourself: "Do we need to prioritise this now?"
          </p>
        </ExpandableCallout>

        <h3 className="text-lg font-semibold text-white mb-3">Trigger Signs:</h3>
        <ul className="list-none space-y-2 text-gray-300">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>Unclear accountabilities of people managers</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>Executive or team member rumbles that managers "don't really seem to do much"</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>Very different "types" of managers across the org (capacity given to role and competency)</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>Declining team morale</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>Inconsistency in managerial practices</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span>High turnover rates among high performers</span>
          </li>
        </ul>
      </div>

      <div className="mt-8 space-y-6">
        <h2 className="text-xl font-semibold text-white mb-4">Inception</h2>
        <p className="text-base text-gray-300 leading-relaxed">
          In our experience, most initiatives like this fail because one person thinks it's a good idea and tries to just start it, without helping those who are going to "do it" also recognise the need first.
        </p>
        <blockquote className="border-l-4 border-gray-700 pl-4 my-4 text-gray-300">
          "I have a dream..." whilst powerful… this isn't quite the right play here.
        </blockquote>
        <p className="text-base text-gray-300 leading-relaxed">
          The key thing for a people manager community of practice is helping people managers see the size of the prize, whilst in parallel ensuring that you have some exec sponsorship (if you're working in the kinda startup where that's important).
        </p>
      </div>

      <div className="mt-8 space-y-6">
        <h2 className="text-xl font-semibold text-white mb-4">Getting Data To Tell A Story</h2>
        <ExpandableCallout
          type="tip"
          title="Example Triggers"
          defaultOpen={true}
        >
          <p className="text-base text-gray-300 leading-relaxed">
            • Team members: receiving vastly different experience of support from one manager to another.
          </p>
          <p className="text-base text-gray-300 leading-relaxed">
            • Managers: lots of folks new to role, limited L&D budget for timely manager training
          </p>
          <p className="text-base text-gray-300 leading-relaxed">
            • Execs: Concern that behaviour issues not being addressed + worried there will be a retention issue.
          </p>
        </ExpandableCallout>

        <h3 className="text-lg font-semibold text-white mb-3">How might we get some data on this?</h3>
        <ol className="list-none space-y-2 text-gray-300">
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">1.</span>
            <span>Slack poll to people managers</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">2.</span>
            <span>121s with all people managers and members of exec asking them some quant questions '1-10 how clear are you on…'</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">3.</span>
            <span>Tweak your pulse survey questions to incorporate question(s) that'll help you get signal on team member perception of manager support.</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">4.</span>
            <span>Co-ordinate an all teams retrospective and ensure the template used gives you signal on 'manager support', 'feedback', 'clarity of performance expectations' and other related data points linked to your triggers</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">5.</span>
            <span>Facilitate a team health check</span>
          </li>
        </ol>
      </div>

      <div className="mt-8 space-y-6">
        <h2 className="text-xl font-semibold text-white mb-4">Using Data to Tell A Story</h2>
        <p className="text-base text-gray-300 leading-relaxed">
          Regardless of what data capture method you employed in the previous section, the next bit is critical. We need a good memo we can share with a friendly exec and ideally at least one people manager who has the credibility and respect of the others. These are your Angels of Change and are going to be important for the next few stages.
        </p>

        <ExpandableCallout
          type="tip"
          title="Angels of Change"
          defaultOpen={true}
        >
          <p className="text-base text-gray-300 leading-relaxed">
            <strong>Executive Angel:</strong> Founder/Exec who believes in the value of people managers and can see the clear line between good people management → good business (profit driven from operational excellence and maximising human potential) to champion the initiative.
          </p>
          <p className="text-base text-gray-300 leading-relaxed">
            <strong>People Manager Angel:</strong> Your most sparky, credible and trusted people manager. This is the person other people managers already go to for help on tricky conversations. This person really gives a shit about their craft and are optimistic by default.
          </p>
        </ExpandableCallout>

        <h3 className="text-lg font-semibold text-white mb-3">The memo should have the following sections:</h3>
        <ol className="list-none space-y-2 text-gray-300">
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">1.</span>
            <span>Intro and hook</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">2.</span>
            <span>This is what the data is telling us about the current status quo (insert themes from data capture exercise)</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">3.</span>
            <span>Highlight the opportunities</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light font-bold mr-2">4.</span>
            <span>Propose: people manager community of practice that meets regularly to hone craft</span>
          </li>
        </ol>
      </div>
    </div>
  );
};

export default ManagerCommunityStage1; 